/**
 * THE PLAN TIER VOCABULARY — one owner, two byte-identical copies (docs/PLAN-per-employee-pricing-20261030.md):
 *   functions/src/billing/planTiers.ts   the server: the plan catalog (planCatalog.ts) charges from these facts
 *   src/config/planTiers.ts              the app: every page that tells a plan's cadence, commitment, unit or edition
 * pinned equal by scripts/check-tier-sniffs.mjs (scripts/gates.d/tier-sniffs.gate), which also fails any test of a
 * tier id's spelling ("starts with wu_", "is wu_monthly") outside this file: a new tier named otherwise would be
 * misread there. Ask these helpers instead.
 *
 * EDITIONS. A catalog edition is a published ladder, sold to new hires while the server sells it and honored for as
 * long as a subscription signed on it stays active (the lock, ruling R6). Editions never mix: a subscription's
 * changes resolve inside its own edition, except that a newer edition in the same unit may only ever lower its price
 * (lockedPrice). An edition's facts never change once sold — a new price is a new edition. Never remove a tier: old
 * webhooks and plan documents still name it.
 *   2026-08  one Prest0n Professional = 2,200 pr0 a seat; rungs at 1-2, 3-4 and 5 or more.
 *   2026-11  one Prest0n employee = 450 pr0 a seat (config/employee-unit.json); rungs at 1-2, 3-8, 9-14, 15-29,
 *            30-59, 60-149 and 150-299; 300 or more book a meeting. Prepay is 20% off every annual rung
 *            (annual cents x 12 x 0.8 a year), a discount, never bonus units.
 *
 * Leaf module: it imports nothing, so the root test suite can import the server's copy directly.
 */

export type CatalogEdition = '2026-08' | '2026-11';

export const CATALOG_EDITIONS: readonly CatalogEdition[] = ['2026-08', '2026-11'];

/**
 * The newest edition: the one the published pages show. The server sells it to new hires once its lever says so
 * (ops/pricingEdition, read fresh by functions/src/billing/pricingEditionLever.ts); until then it sells
 * PREVIOUS_EDITION, so a deploy that is still rolling out never sells a tier an old revision cannot read.
 */
export const PUBLISHED_EDITION: CatalogEdition = '2026-11';

/** The edition the server sells while its lever is off. */
export const PREVIOUS_EDITION: CatalogEdition = '2026-08';

export type PlanCadence = 'monthly' | 'annual' | 'prepay';

export const PLAN_CADENCES: readonly PlanCadence[] = ['monthly', 'annual', 'prepay'];

export type LadderTier =
  | 'wu_monthly'
  | 'wu_annual'
  | 'wu_annual_team3'
  | 'wu_annual_team5'
  | 'wu_prepay'
  | 'wu_prepay_team3'
  | 'wu_prepay_team5'
  | 'emp_monthly'
  | 'emp_annual_1'
  | 'emp_annual_3'
  | 'emp_annual_9'
  | 'emp_annual_15'
  | 'emp_annual_30'
  | 'emp_annual_60'
  | 'emp_annual_150'
  | 'emp_prepay_1'
  | 'emp_prepay_3'
  | 'emp_prepay_9'
  | 'emp_prepay_15'
  | 'emp_prepay_30'
  | 'emp_prepay_60'
  | 'emp_prepay_150';

export interface LadderTierFacts {
  edition: CatalogEdition;
  cadence: PlanCadence;
  /** The Stripe recurring interval the per-seat cents bill on (a prepaid year bills once a year). */
  interval: 'month' | 'year';
  /** Cents per seat per interval: what Stripe charges. */
  perSeatCents: number;
  /** The rung's team sizes, inclusive (a prepaid rung takes the same sizes as its annual rung). */
  minSeats: number;
  maxSeats: number;
  /** pr0 per seat a month the tier is priced in: 2,200 on edition 2026-08, one Prest0n employee on 2026-11. */
  unitPr0: number;
  /** Months committed; 0 for a monthly hire, which can end any month. */
  commitmentMonths: number;
}

export const LADDER_TIERS: Readonly<Record<LadderTier, LadderTierFacts>> = {
  // ── edition 2026-08: one Prest0n Professional, 2,200 pr0 a seat (sold until edition 2026-11; honored while active) ──
  wu_monthly: { edition: '2026-08', cadence: 'monthly', interval: 'month', perSeatCents: 375000, minSeats: 1, maxSeats: 500, unitPr0: 2200, commitmentMonths: 0 },
  wu_annual: { edition: '2026-08', cadence: 'annual', interval: 'month', perSeatCents: 320000, minSeats: 1, maxSeats: 2, unitPr0: 2200, commitmentMonths: 12 },
  wu_annual_team3: { edition: '2026-08', cadence: 'annual', interval: 'month', perSeatCents: 295000, minSeats: 3, maxSeats: 4, unitPr0: 2200, commitmentMonths: 12 },
  wu_annual_team5: { edition: '2026-08', cadence: 'annual', interval: 'month', perSeatCents: 275000, minSeats: 5, maxSeats: 500, unitPr0: 2200, commitmentMonths: 12 },
  wu_prepay: { edition: '2026-08', cadence: 'prepay', interval: 'year', perSeatCents: 3072000, minSeats: 1, maxSeats: 2, unitPr0: 2200, commitmentMonths: 12 },
  wu_prepay_team3: { edition: '2026-08', cadence: 'prepay', interval: 'year', perSeatCents: 2832000, minSeats: 3, maxSeats: 4, unitPr0: 2200, commitmentMonths: 12 },
  wu_prepay_team5: { edition: '2026-08', cadence: 'prepay', interval: 'year', perSeatCents: 2640000, minSeats: 5, maxSeats: 500, unitPr0: 2200, commitmentMonths: 12 },
  // ── edition 2026-11: one Prest0n employee, 450 pr0 a seat; $ per pr0 x 450, rounded half up ──
  emp_monthly: { edition: '2026-11', cadence: 'monthly', interval: 'month', perSeatCents: 76500, minSeats: 1, maxSeats: 299, unitPr0: 450, commitmentMonths: 0 },
  emp_annual_1: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 69800, minSeats: 1, maxSeats: 2, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_3: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 65300, minSeats: 3, maxSeats: 8, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_9: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 60300, minSeats: 9, maxSeats: 14, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_15: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 56300, minSeats: 15, maxSeats: 29, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_30: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 52700, minSeats: 30, maxSeats: 59, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_60: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 49500, minSeats: 60, maxSeats: 149, unitPr0: 450, commitmentMonths: 12 },
  emp_annual_150: { edition: '2026-11', cadence: 'annual', interval: 'month', perSeatCents: 46800, minSeats: 150, maxSeats: 299, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_1: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 670080, minSeats: 1, maxSeats: 2, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_3: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 626880, minSeats: 3, maxSeats: 8, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_9: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 578880, minSeats: 9, maxSeats: 14, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_15: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 540480, minSeats: 15, maxSeats: 29, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_30: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 505920, minSeats: 30, maxSeats: 59, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_60: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 475200, minSeats: 60, maxSeats: 149, unitPr0: 450, commitmentMonths: 12 },
  emp_prepay_150: { edition: '2026-11', cadence: 'prepay', interval: 'year', perSeatCents: 449280, minSeats: 150, maxSeats: 299, unitPr0: 450, commitmentMonths: 12 },
};

/** Every ladder tier, in the table's order. */
export const LADDER_TIER_IDS: readonly LadderTier[] = Object.keys(LADDER_TIERS) as LadderTier[];

/** True for a tier of a published per-seat ladder, of any edition. */
export function isLadderTier(tier: unknown): tier is LadderTier {
  return typeof tier === 'string' && Object.prototype.hasOwnProperty.call(LADDER_TIERS, tier);
}

/** A ladder tier's facts, or null for anything else (the grandfathered starter/professional/enterprise, unknown). */
export function tierFacts(tier: unknown): LadderTierFacts | null {
  return isLadderTier(tier) ? LADDER_TIERS[tier] : null;
}

/** monthly (no commitment), annual (a 12-month commitment billed monthly) or prepay (the year in one payment). */
export function cadenceOfTier(tier: unknown): PlanCadence | null {
  return tierFacts(tier)?.cadence ?? null;
}

export function editionOfTier(tier: unknown): CatalogEdition | null {
  return tierFacts(tier)?.edition ?? null;
}

/** pr0 per seat a month the tier is priced in. */
export function unitOfTier(tier: unknown): number | null {
  return tierFacts(tier)?.unitPr0 ?? null;
}

/** Months committed (0 for monthly); null when the tier is not on a ladder. */
export function commitmentMonthsOfTier(tier: unknown): number | null {
  const f = tierFacts(tier);
  return f ? f.commitmentMonths : null;
}

/** True when the tier binds a term (annual or prepaid). */
export function isCommittedTier(tier: unknown): boolean {
  return (commitmentMonthsOfTier(tier) ?? 0) > 0;
}

/** True when the tier is on the edition the published pages show. */
export function isPublishedTier(tier: unknown): boolean {
  return editionOfTier(tier) === PUBLISHED_EDITION;
}

/** An edition word, or null for anything else. */
export function asEdition(value: unknown): CatalogEdition | null {
  return value === '2026-08' || value === '2026-11' ? value : null;
}

/** A cadence word, or null for anything else. */
export function asCadence(value: unknown): PlanCadence | null {
  return value === 'monthly' || value === 'annual' || value === 'prepay' ? value : null;
}

/** One edition's tiers for a cadence, smallest team first. */
export function rungTiers(edition: CatalogEdition, cadence: PlanCadence): LadderTier[] {
  return LADDER_TIER_IDS
    .filter((t) => LADDER_TIERS[t].edition === edition && LADDER_TIERS[t].cadence === cadence)
    .sort((a, b) => LADDER_TIERS[a].minSeats - LADDER_TIERS[b].minSeats);
}

/** The most seats an edition sells on its own (larger organizations book a meeting). */
export function maxSeatsOf(edition: CatalogEdition): number {
  return Math.max(...LADDER_TIER_IDS.filter((t) => LADDER_TIERS[t].edition === edition).map((t) => LADDER_TIERS[t].maxSeats));
}

/**
 * The tier an edition sells for a cadence and team size: the rung whose sizes hold it. Null for an unknown cadence and
 * for a size outside the edition's self-serve range (0, a fraction, or past the ceiling), which the caller refuses.
 * The edition is always named: a change to a live plan resolves in that plan's own edition, a new hire in the edition
 * the server sells (a default here is how a 2026-08 subscription would have been moved onto another unit unseen).
 */
export function tierForSelection(cadence: unknown, seats: number, edition: CatalogEdition): LadderTier | null {
  const c = asCadence(cadence);
  if (!c || !Number.isInteger(seats) || seats < 1) return null;
  return rungTiers(edition, c).find((t) => seats >= LADDER_TIERS[t].minSeats && seats <= LADDER_TIERS[t].maxSeats) ?? null;
}

/** Monthly-equivalent cents per seat (a prepaid year's cents over twelve). */
export function monthlyEquivalentCentsPerSeat(tier: unknown): number | null {
  const f = tierFacts(tier);
  if (!f) return null;
  return f.interval === 'year' ? Math.round(f.perSeatCents / 12) : f.perSeatCents;
}

/**
 * The lock (ruling R6): the price card a subscription signed on never rises while it stays active. This is the tier
 * and per-seat cents a subscription on `cohortTier` pays at a cadence and team size: its own edition's rung for that
 * size and cadence (fewer employees, or monthly, is that card's dearer rung), unless the edition the server sells now
 * is priced in the same unit and its rung for that size costs less — "if ours falls, yours falls". A different unit
 * never crosses editions, so a 2026-08 subscription stays on its own ladder for as long as it stays active. Null when
 * the size is outside the cohort edition's range (the caller refuses, or books a meeting).
 */
export function lockedPrice(cohortTier: LadderTier, cadence: PlanCadence, seats: number, sellingEdition: CatalogEdition): { tier: LadderTier; perSeatCents: number } | null {
  const cohort = LADDER_TIERS[cohortTier];
  const own = tierForSelection(cadence, seats, cohort.edition);
  if (!own) return null;
  const ownCents = LADDER_TIERS[own].perSeatCents;
  if (cohort.edition === sellingEdition) return { tier: own, perSeatCents: ownCents };
  const current = tierForSelection(cadence, seats, sellingEdition);
  if (current && LADDER_TIERS[current].unitPr0 === cohort.unitPr0 && LADDER_TIERS[current].perSeatCents < ownCents) {
    return { tier: current, perSeatCents: LADDER_TIERS[current].perSeatCents };
  }
  return { tier: own, perSeatCents: ownCents };
}

// ── The one resolver: a hire or a change, from what the person chose, to the tier and its facts ──
// Shared by every door that sells or changes a plan (subscribeDirect, the Order prepare, changeSubscription,
// armTrialConversion and the conversion's facts) and by the app's own pre-check, so the page never offers what the
// server refuses. It refuses, never clamps: a size the edition does not sell on its own is a meeting, a tier other than
// the rung for the size is a re-quote, and an edition the server does not sell is not for sale.

export type ResolveRefusal =
  | 'bad-cadence'
  | 'bad-seats'
  | 'by-meeting'
  | 'tier-mismatch'
  | 'edition-not-on-sale'
  | 'cross-edition';

export interface ResolvedPlan {
  ok: true;
  tier: LadderTier;
  cadence: PlanCadence;
  seats: number;
  edition: CatalogEdition;
  perSeatCents: number;
  interval: 'month' | 'year';
  commitmentMonths: number;
  unitPr0: number;
}

export interface ResolveRefused {
  ok: false;
  refusal: ResolveRefusal;
  /** For a tier-mismatch: the tier and cents the size is sold at, so the page can re-quote. */
  expectedTier?: LadderTier;
  expectedPerSeatCents?: number;
  /** For by-meeting: the most this edition sells on its own. */
  maxSeats?: number;
}

function resolved(tier: LadderTier, seats: number, perSeatCents = LADDER_TIERS[tier].perSeatCents): ResolvedPlan {
  const f = LADDER_TIERS[tier];
  return { ok: true, tier, cadence: f.cadence, seats, edition: f.edition, perSeatCents, interval: f.interval, commitmentMonths: f.commitmentMonths, unitPr0: f.unitPr0 };
}

function sizeRefusal(seats: unknown, edition: CatalogEdition): ResolveRefused | null {
  if (typeof seats !== 'number' || !Number.isInteger(seats) || seats < 1) return { ok: false, refusal: 'bad-seats' };
  const max = maxSeatsOf(edition);
  if (seats > max) return { ok: false, refusal: 'by-meeting', maxSeats: max };
  return null;
}

/**
 * A new hire: the rung the selling edition sells for this cadence and size. `requestedTier`, when the page sent one,
 * must be exactly that rung (a cheap rung's tier with too many seats, or another edition's, is refused, never
 * charged). The cadence may come from the requested tier when none is named.
 */
export function resolveHire(input: { cadence?: unknown; seats: unknown; requestedTier?: unknown; sellingEdition: CatalogEdition }): ResolvedPlan | ResolveRefused {
  const requested = input.requestedTier === undefined || input.requestedTier === null ? null : input.requestedTier;
  if (requested !== null && !isLadderTier(requested)) return { ok: false, refusal: 'tier-mismatch' };
  const cadence = asCadence(input.cadence) ?? (requested !== null ? LADDER_TIERS[requested as LadderTier].cadence : null);
  if (!cadence) return { ok: false, refusal: 'bad-cadence' };
  if (requested !== null && LADDER_TIERS[requested as LadderTier].edition !== input.sellingEdition) return { ok: false, refusal: 'edition-not-on-sale' };
  const size = sizeRefusal(input.seats, input.sellingEdition);
  if (size) return size;
  const tier = tierForSelection(cadence, input.seats as number, input.sellingEdition);
  if (!tier) return { ok: false, refusal: 'bad-seats' };
  if (requested !== null && requested !== tier) {
    return { ok: false, refusal: 'tier-mismatch', expectedTier: tier, expectedPerSeatCents: LADDER_TIERS[tier].perSeatCents };
  }
  return resolved(tier, input.seats as number);
}

/**
 * A change to a live plan on `currentTier`: the size and cadence resolve inside that plan's own edition at its locked
 * price card (lockedPrice), never onto another unit. A requested tier from another edition is a cross-edition change,
 * refused: the plan keeps its edition for as long as it stays active.
 */
export function resolveChange(input: { currentTier: unknown; cadence?: unknown; seats: unknown; requestedTier?: unknown; sellingEdition: CatalogEdition }): ResolvedPlan | ResolveRefused {
  if (!isLadderTier(input.currentTier)) return { ok: false, refusal: 'cross-edition' };
  const current = LADDER_TIERS[input.currentTier];
  const requested = input.requestedTier === undefined || input.requestedTier === null ? null : input.requestedTier;
  if (requested !== null && (!isLadderTier(requested) || LADDER_TIERS[requested].unitPr0 !== current.unitPr0)) return { ok: false, refusal: 'cross-edition' };
  const cadence = asCadence(input.cadence) ?? (requested !== null ? LADDER_TIERS[requested as LadderTier].cadence : current.cadence);
  if (!cadence) return { ok: false, refusal: 'bad-cadence' };
  const size = sizeRefusal(input.seats, current.edition);
  if (size) return size;
  const price = lockedPrice(input.currentTier, cadence, input.seats as number, input.sellingEdition);
  if (!price) return { ok: false, refusal: 'bad-seats' };
  if (requested !== null && requested !== price.tier && LADDER_TIERS[requested as LadderTier].edition !== LADDER_TIERS[price.tier].edition) {
    return { ok: false, refusal: 'cross-edition' };
  }
  if (requested !== null && requested !== price.tier) {
    return { ok: false, refusal: 'tier-mismatch', expectedTier: price.tier, expectedPerSeatCents: price.perSeatCents };
  }
  return resolved(price.tier, input.seats as number, price.perSeatCents);
}
